import * as React from 'react';
import classnames from 'classnames';
import Flex, { Props as FlexProps } from '../Flex/Flex';
import './Button.css';

export interface Props extends FlexProps {
  /**
   * Removes the borders from the Button, useful for when used inside a w
   * rapping container with custom borders
   */
  borderless?: boolean;
  /**
   * Allows the Button to fill the container space rather than shrinking
   * down to the content
   */
  fullWidth?: boolean;
  /** Sets the visual importance of the Button. */
  level?: 'primary' | 'secondary' | 'tertiary';
  /**
   * Sets the size of the Button. Small allows the Button to fit
   * into smaller spaces, while large will help the button stand
   * out on a page with lots of space.
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Variant is another prop that changes the styles to indicate it's usage.
   * For example "danger" is to let the user know this button performs a
   * potentionally dangerous action.
   */
  variant?: 'danger';
}

/** Standard Button component */
const Button: React.FunctionComponent<Props> = (props: Props) => {
  const {
    borderless,
    fullWidth,
    level,
    size,
    variant,
    ...rest
  } = props;

  const gap = size === 'small' ? 'x1' : 'x2';
  const classes = classnames('Button',
    `Button--level-${level}`,
    `Button--size-${size}`, {
      'Button--borderless': borderless,
      'Button--full-width': fullWidth,
      [`Button--variant-${variant}`]: variant,
    });

  return (
    <Flex Component="button" { ...rest }
        alignChildren="middle"
        className={ classes }
        direction="horizontal"
        gap={ gap } />
  );
};

Button.defaultProps = {
  level: 'secondary',
  size: 'medium',
};

export default Button;
